import { useEffect, useRef } from "react";
import { connect } from "react-redux";
import moment from "moment";
import { addDaysToDate, parseAsLocalDate } from "../../../utils";
import { between, createRng, round } from "../art/rng";
import SacredMark from "./SacredMark";
import Scenery from "./Scenery";
import Toran from "../art/Toran";
import { coupleNames } from "../helpers";
import { segment, useScrollScene } from "../hooks";
import ScrollCue from "../../ScrollCue/ScrollCue";

const rng = createRng(53);
const MOTES = Array.from({ length: 16 }, () => ({
  left: round(between(rng, 4, 96)),
  bottom: round(between(rng, 4, 40)),
  size: round(between(rng, 2, 4.5)),
  duration: round(between(rng, 9, 16)),
  delay: round(between(rng, -16, 0)),
}));

/** Splits a name into letters so each one can rise in turn. */
const Letters = ({ text, start }) => (
  <span className="rm-hero__name" aria-label={text}>
    {[...text].map((char, i) => (
      <span key={i} aria-hidden="true" style={{ "--i": start + i }}>
        {char}
      </span>
    ))}
  </span>
);


/**
 * The first page, as in the other themes: the couple's names and the wedding date
 * under a bougainvillea toran, above the lake palace with birds in the sky. Scrolling
 * away zooms gently into the palace.
 */
const Hero = ({ bride, groom, eventDate, isPreview }) => {
  const ref = useRef(null);
  useScrollScene(ref, {
    mode: "exit",
    vars: (p, reduced) => ({ "--z": reduced ? 0 : segment(p, 0, 0.9) }),
  });

  // Keep the birds' stretch of sky just below the title, wherever the title ends
  const titleRef = useRef(null);
  useEffect(() => {
    const section = ref.current;
    const title = titleRef.current;
    if (!section || !title || typeof ResizeObserver === "undefined") return undefined;
    const measure = () => {
      if (!section.clientHeight) return;
      const bottom = ((title.offsetTop + title.offsetHeight) / section.clientHeight) * 100;
      section.style.setProperty("--rm-sky-top", `${bottom.toFixed(1)}%`);
    };
    const observer = new ResizeObserver(measure);
    observer.observe(section);
    observer.observe(title);
    return () => observer.disconnect();
  }, []);

  const { first, second } = coupleNames(bride, groom);
  const date = isPreview ? addDaysToDate(16) : parseAsLocalDate(eventDate);
  // addDaysToDate returns a date string; wrap in Date so moment doesn't parse a non-ISO string.
  const day = date ? moment(new Date(date)) : null;

  return (
    <section ref={ref} className="rm-hero" aria-label="Wedding invitation">
      <div className="rm-hero__sun" aria-hidden="true" />
      <Scenery className="rm-hero__scenery" />
      <div className="rm-hero__motes" aria-hidden="true">
        {MOTES.map((m, i) => (
          <span
            key={i}
            style={{
              left: `${m.left}%`,
              bottom: `${m.bottom}%`,
              width: m.size,
              height: m.size,
              animationDuration: `${m.duration}s`,
              animationDelay: `${m.delay}s`,
            }}
          />
        ))}
      </div>

      <Toran className="rm-hero__toran" />

      <div ref={titleRef} className="rm-hero__title">
        <SacredMark className="rm-hero__emblem" />
        <p className="rm-kicker rm-hero__intro">
          Together with our families, we joyfully invite you to celebrate the wedding of
        </p>
        <h1 className="rm-hero__names">
          <Letters text={first} start={0} />
          <span className="rm-script rm-hero__amp">&amp;</span>
          <Letters text={second} start={first.length + 2} />
        </h1>
        <p className="rm-script rm-hero__vow-script">on</p>
        {day && (
          <div className="rm-hero__save">
            <p className="rm-hero__date" aria-label={day.format("dddd, D MMMM YYYY")}>
              <span className="rm-hero__date-side" aria-hidden="true">
                {day.format("dddd")}
              </span>
              <span className="rm-hero__date-day" aria-hidden="true">
                {day.format("DD")}
              </span>
              <span className="rm-hero__date-side" aria-hidden="true">
                {day.format("MMMM")}
              </span>
            </p>
            <p className="rm-kicker rm-hero__date-year" aria-hidden="true">
              {day.format("YYYY")}
            </p>
          </div>
        )}
      </div>

      <ScrollCue />
    </section>
  );
};

const mapStateToProps = (state) => ({
  bride: state.inviteDetails.invite?.bride,
  groom: state.inviteDetails.invite?.groom,
  eventDate: state.inviteDetails.invite?.eventDate,
  isPreview: state.inviteDetails?.isPreview,
});

export default connect(mapStateToProps)(Hero);
